/**
 * Reconciliation — wireframe 3 (W2-11): the month's actual work.
 *
 * One read paints everything: the import version, the suggestions (a person
 * accepts, or nothing happens), the stale re-confirmations, both worklists
 * with aging, and the eligible-ITC panel whose every deviation is named.
 */

import { Alert, Button, Card } from '@fiveox/ui'
import { Play, Scale, Upload } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useEvaluation } from '../lib/access'
import {
  ApiError,
  type ReconState,
  type WorklistEntry,
  recon,
  reference,
} from '../lib/api'
import { useClient } from './ClientLayout'

const RECON = 'recon'

/** Indian digit grouping — the legacy renders 6,56,755.08, not 656,755.08. */
function rupees(paise: number | null | undefined): string {
  if (paise === null || paise === undefined) return '—'
  return (paise / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export function Recon() {
  const client = useClient()
  const { can } = useEvaluation()

  // The client comes from the URL (ND-C1). This screen used to carry its own
  // picker, which meant the sidebar, the address bar and this dropdown could
  // each name a different client at the same time.
  const clientId = client.id
  const [period, setPeriod] = useState('')
  const [state, setState] = useState<ReconState | null>(null)
  const [reasons, setReasons] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [suppressing, setSuppressing] = useState<{ key: string; kind: 'books' | 'tb' } | null>(null)
  const [reason, setReason] = useState('')
  const fileInput = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    if (!clientId || !/^\d{2}-\d{4}$/.test(period)) {
      setState(null)
      return
    }
    try {
      const [stateBody, reasonBody] = await Promise.all([
        recon.state(clientId, period),
        reference.category('suppress_reason', period),
      ])
      setState(stateBody.data)
      setReasons(reasonBody.data.values.map((v) => v.key))
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Reconciliation could not be loaded.')
    }
  }, [clientId, period])

  useEffect(() => {
    void load()
  }, [load])

  const act = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true)
      setError(null)
      try {
        await fn()
        await load()
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That action failed.')
      } finally {
        setBusy(false)
      }
    },
    [load],
  )

  const importFile = useCallback(
    async (files: FileList | null) => {
      const file = files?.[0]
      if (!file || !clientId || !period) return
      await act(async () => {
        // The portal hands out 2B as a workbook; JSON is the fallback for a
        // file someone already converted. The extension decides, so an
        // operator drops in whatever the portal gave them.
        if (/\.xlsx?$/i.test(file.name)) {
          await recon.importWorkbook({ file, client_id: clientId, period })
        } else {
          const rows = JSON.parse(await file.text()) as Array<Record<string, unknown>>
          await recon.importRows({
            client_id: clientId,
            period,
            rows,
            source_filename: file.name,
          })
        }
        await recon.run({ client_id: clientId, period })
      })
    },
    [act, clientId, period],
  )

  const suppress = useCallback(
    (entry: WorklistEntry, kind: 'books' | 'tb') =>
      act(() =>
        recon.act({
          type: 'suppress',
          client_id: clientId,
          period,
          books_content_key: kind === 'books' ? entry.content_key : undefined,
          tb_row_key: kind === 'tb' ? entry.tb_row_key : undefined,
          reason_code: reason,
        }),
      ).then(() => {
        setSuppressing(null)
        setReason('')
      }),
    [act, clientId, period, reason],
  )

  const worklistTable = (entries: WorklistEntry[], kind: 'books' | 'tb', title: string) => (
    <div className="min-w-0 flex-1">
      <h3 className="mb-2 text-sm font-medium text-foreground">{title}</h3>
      {entries.length === 0 ? (
        <div className="gst-empty">Nothing here.</div>
      ) : (
        <div className="gst-table-wrap">
          <table className="gst-table">
            <thead>
              <tr>
                <th className="is-text">Doc</th>
                <th className="is-text">Supplier</th>
                <th>Taxable (₹)</th>
                <th>Age</th>
                <th className="is-text">Next</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, i) => {
                const key = (kind === 'books' ? entry.content_key : entry.tb_row_key) ?? ''
                return (
                  <tr key={key} className={i % 2 === 1 ? 'is-a' : ''}>
                    <td className="is-text is-sec">
                      {entry.doc_no}
                      <small>{entry.doc_date}</small>
                      {entry.carried_from && (
                        <span className="gst-tag" title="Booked in an earlier month and still waiting for the supplier's 2B (D-38)">
                          booked {entry.carried_from}
                        </span>
                      )}
                    </td>
                    <td className="is-text">
                      <span className="gst-mono">{entry.supplier_gstin}</span>
                    </td>
                    <td>{rupees(entry.taxable)}</td>
                    <td>{entry.age_days !== null ? `${entry.age_days}d` : '—'}</td>
                    <td className="is-text">
                      <span title={entry.risk}>{entry.risk}</span>
                      {can(RECON, 'can_update') && (
                        <span className="mt-1 flex flex-wrap items-center gap-1">
                          {suppressing?.key === key ? (
                            <>
                              <select
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                className="gst-select"
                              >
                                <option value="">Reason…</option>
                                {reasons.map((code) => (
                                  <option key={code} value={code}>
                                    {code.replaceAll('_', ' ')}
                                  </option>
                                ))}
                              </select>
                              <Button
                                type="button"
                                disabled={!reason || busy}
                                onClick={() => void suppress(entry, kind)}
                                variant="outline" size="sm"
                              >
                                Suppress
                              </Button>
                              <Button
                                type="button"
                                onClick={() => setSuppressing(null)}
                                variant="outline" size="sm"
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <Button
                              type="button"
                              onClick={() => setSuppressing({ key, kind })}
                              variant="outline" size="sm"
                            >
                              Suppress…
                            </Button>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="gst-h1">
            <Scale size={20} /> Reconciliation
          </h1>
          <p className="gst-sub">
            Books vs 2B — every rupee of ITC traces to a match, an action, or a named gate
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="gst-label">
            Period
            <input
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder="07-2026"
              className="gst-input w-24"
            />
          </label>
          {can(RECON, 'can_create') && (
            <>
              <Button
                type="button"
                disabled={!clientId || !period || busy}
                onClick={() => fileInput.current?.click()}
                size="sm"
              >
                <Upload size={15} /> Import 2B
              </Button>
              <input
                ref={fileInput}
                type="file"
                accept=".xlsx,.xls,application/json,.json"
                hidden
                onChange={(e) => void importFile(e.target.files)}
              />
            </>
          )}
          {can(RECON, 'can_update') && state?.import && (
            <Button
              type="button"
              disabled={busy}
              onClick={() => void act(() => recon.run({ client_id: clientId, period }))}
              variant="outline" size="sm"
            >
              <Play size={15} /> Re-run matching
            </Button>
          )}
        </div>
      </header>

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}

      {!state ? (
        <p className="gst-sub">Pick a client and a period (MM-YYYY).</p>
      ) : (
        <>
          {/* ITC panel — the month's answer, deviations named. The legacy states
              the rule on the panel; so does this. */}
          <p className="gst-sub mb-2">CLAIMABLE ITC = min(Books, 2B)</p>
          <div className="gst-stats mb-6">
            {(['igst', 'cgst', 'sgst', 'cess'] as const).map((head) => (
              <div key={head} className="gst-stat">
                <div className="n is-money">₹{rupees(state.itc.eligible[head] ?? 0)}</div>
                <div className="l">{head.toUpperCase()} ELIGIBLE</div>
                {(state.itc.held_stale[head] ?? 0) > 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    + {rupees(state.itc.held_stale[head])} held (stale — re-confirm)
                  </p>
                )}
                {(state.itc.disputed[head] ?? 0) > 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    {rupees(state.itc.disputed[head])} disputed
                  </p>
                )}
              </div>
            ))}
          </div>

          <p className="gst-meta mb-6">
            {state.import
              ? `2B v${state.import.version_no} · ${state.import.row_count} rows · ` +
                `+${state.import.diff.added ?? 0} / −${state.import.diff.removed ?? 0} / ~${state.import.diff.changed ?? 0} vs previous`
              : 'No 2B imported for this period yet.'}
            {' · '}ITC where 2B lags: {state.itc.fallback.state.replace('_', ' ')} —{' '}
            {state.itc.fallback.gate}
          </p>

          {/* suggestions + stale — a person decides */}
          {(state.suggested.length > 0 || state.stale.length > 0) && can(RECON, 'can_update') && (
            <Alert tone="warning" className="mb-6">
              <p className="gst-panel-h">Waiting on you — nothing matches itself</p>
              <ul className="space-y-1">
                {[...state.suggested, ...state.stale].map((match) => (
                  <li key={match.id} className="flex flex-wrap items-center gap-2 text-sm text-foreground">
                    <span>
                      {match.status === 'stale' ? 'Re-confirm' : 'Suggested'} ·{' '}
                      {match.rung}
                      {match.evidence?.score !== undefined &&
                        ` (score ${String(match.evidence.score)})`}
                      <span className="block font-mono text-[11px] text-muted-foreground">
                        books {match.books_content_key.slice(0, 12)} ↔ 2B{' '}
                        {match.tb_row_key.slice(0, 12)}
                      </span>
                    </span>
                    <Button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        void act(() =>
                          recon.act({
                            type: 'accept',
                            client_id: clientId,
                            period,
                            match_id: match.id,
                          }),
                        )
                      }
                      variant="outline" size="sm"
                    >
                      {match.status === 'stale' ? 'Re-confirm' : 'Accept'}
                    </Button>
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {state.books_notes.length > 0 && (
            <Card className="mb-4 p-3 text-xs text-muted-foreground">
              {/* Each note is a different kind of thing now — a row skipped, rows
                  held in review, purchases carried from earlier months, rows past
                  the time limit — so they are listed, not summarised as "skipped". */}
              <ul className="list-disc space-y-1 pl-4">
                {state.books_notes.slice(0, 5).map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
              {state.books_notes.length > 5 && (
                <p className="mt-1">and {state.books_notes.length - 5} more</p>
              )}
            </Card>
          )}

          <div className="flex flex-wrap gap-6">
            {worklistTable(state.worklists.books_only, 'books', 'Books-only — ITC at risk')}
            {worklistTable(state.worklists.tb_only, 'tb', '2B-only — books missing')}
          </div>
        </>
      )}
    </div>
  )
}
